<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useSessionStore, useSignalRStore, useUserStore } from '../../../stores';
import { Session, VocabItem } from '../../../types/session';
import { UserRole } from '../../../types/user';

const user = useUserStore()
const sessionStore = useSessionStore()
const signalr = useSignalRStore()

const vocabItemRefs = ref<(HTMLElement | null)[]>([])

const isSelectable = computed<boolean>(() => {
    if (user.userRole === UserRole.student && sessionStore.allowSpeakingStudentIds.includes(user.userInfo?.id || 0) && sessionStore.teacherVocabId) {
        return false;
    }
    return true
})

const vocabList = computed<VocabItem[]>(() => {
    const v = sessionStore.classContent?.[Session.vocab];
    return Array.isArray(v) ? v : [];
});

function handleVocabItemClick(vocabItem: VocabItem) {
    if (!isSelectable.value) return;

    sessionStore.currentVocabId = vocabItem.id

    if (user.userRole === UserRole.teacher && sessionStore.teacherVocabId) {
        signalr.teacherChangeVocab()
    }
}

onMounted(() => {
    if (sessionStore.currentVocabId) return;
    const vocab = sessionStore.classContent?.[Session.vocab]

    if (!Array.isArray(vocab) || !vocab[0]?.id) return;
    sessionStore.currentVocabId = vocab[0]?.id
    // set default to first vocab item in list
})

watchEffect(() => {
    const vocab = sessionStore.classContent?.[Session.vocab]
    if (!Array.isArray(vocab) || !vocab[0]?.id) return;
    const vocabItemIndex: number = vocab.findIndex(vocabItem => vocabItem.id === sessionStore.currentVocabId) ?? -1
    if (vocabItemIndex < 0) return;

    const vocabItemRef = vocabItemRefs.value[vocabItemIndex]
    const container = vocabItemRef?.parentElement
    if (!vocabItemRef || !container) return;

    const elementRect = vocabItemRef.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const isVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom
    if (isVisible) return;

    vocabItemRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
})
</script>

<template>
    <div class="flex flex-col gap-3">
        <div v-for="vocabItem in vocabList" class="border-[1px] border-b-4 lg:border-b-8 border-neutral-200 px-3 py-2 lg:px-6 lg:py-5 rounded-lg lg:rounded-xl font-bold text-neutral-500 lg:text-lg transition-all" :class="{
            '!border-sky-500 !bg-sky-100 !text-sky-500': sessionStore.currentVocabId === vocabItem.id,
            'hover:border-sky-500 hover:bg-sky-100 hover:text-sky-500 cursor-pointer': isSelectable,
            'bg-neutral-200 border-neutral-300 cursor-not-allowed': !isSelectable
        }" @click="handleVocabItemClick(vocabItem)">
            {{ vocabItem.word }}
        </div>
    </div>
</template>